import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export function LoginPage() {
  const { login, kidLogin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("parent"); // "parent" | "kid"

  // Parent login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Kid login state
  const [parentEmail, setParentEmail] = useState("");
  const [nickname, setNickname] = useState("");
  const [pin, setPin] = useState("");

  const [busy, setBusy] = useState(false);

  const submitParent = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const submitKid = async (e) => {
    e.preventDefault();
    if (pin.length < 4) {
      toast.error("PIN must be at least 4 digits");
      return;
    }
    setBusy(true);
    try {
      await kidLogin(parentEmail, nickname, pin);
      toast.success("Welcome back, hero!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen halftone-pink">
      <Navbar />
      <div className="max-w-md mx-auto p-6 mt-12">
        <div className="cv-border cv-shadow-lg rounded-2xl bg-white p-8">
          <h1
            className="font-headings uppercase text-4xl mb-2"
            data-testid="login-title"
          >
            Welcome back!
          </h1>

          {/* Tab switcher */}
          <div className="grid grid-cols-2 gap-2 mb-6">
            <button
              type="button"
              onClick={() => setTab("parent")}
              className={`cv-border-3 rounded-xl py-2 font-headings uppercase cv-press ${
                tab === "parent" ? "bg-yellow-300 cv-shadow-sm" : "bg-white"
              }`}
              data-testid="login-tab-parent"
            >
              Parent
            </button>
            <button
              type="button"
              onClick={() => setTab("kid")}
              className={`cv-border-3 rounded-xl py-2 font-headings uppercase cv-press ${
                tab === "kid" ? "bg-pink-300 cv-shadow-sm" : "bg-white"
              }`}
              data-testid="login-tab-kid"
            >
              Kid 🦸
            </button>
          </div>

          {tab === "parent" ? (
            <>
              <p className="font-body font-bold mb-6 text-black/70">
                Sign in as a parent
              </p>
              <form onSubmit={submitParent} className="space-y-4">
                <div>
                  <Label className="font-headings uppercase">Email</Label>
                  <Input
                    className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email"
                  />
                </div>
                <div>
                  <Label className="font-headings uppercase">Password</Label>
                  <Input
                    className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-yellow-400 hover:bg-yellow-300 text-black cv-border cv-shadow cv-press font-headings text-xl uppercase rounded-xl py-6"
                  data-testid="login-submit"
                >
                  {busy ? "Signing in..." : "Sign in"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <p className="font-body font-bold mb-6 text-black/70">
                Sign in as a kid
              </p>
              <form onSubmit={submitKid} className="space-y-4">
                <div>
                  <Label className="font-headings uppercase">
                    Parent's Email
                  </Label>
                  <Input
                    className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                    type="email"
                    required
                    value={parentEmail}
                    onChange={(e) => setParentEmail(e.target.value)}
                    data-testid="kid-login-parent-email"
                  />
                </div>
                <div>
                  <Label className="font-headings uppercase">Nickname</Label>
                  <Input
                    className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                    type="text"
                    required
                    pattern="[a-zA-Z0-9_\-]+"
                    title="Letters, numbers, underscores and dashes only"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    data-testid="kid-login-nickname"
                  />
                </div>
                <div>
                  <Label className="font-headings uppercase">PIN</Label>
                  <Input
                    className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                    type="password"
                    required
                    pattern="[0-9]+"
                    minLength={4}
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    data-testid="kid-login-pin"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-pink-500 hover:bg-pink-400 text-white cv-border cv-shadow cv-press font-headings text-xl uppercase rounded-xl py-6"
                  data-testid="kid-login-submit"
                >
                  {busy ? "Signing in..." : "Let's go! 🚀"}
                </Button>
              </form>
            </>
          )}

          <p className="mt-6 font-body font-bold text-sm text-center">
            No account?{" "}
            <Link
              to="/signup"
              className="text-purple-600 underline"
              data-testid="login-to-signup"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function SignupPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    parent_pin: "",
    role: "parent",
  });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.parent_pin.length < 4) {
      toast.error("PIN must be at least 4 digits");
      return;
    }
    setBusy(true);
    try {
      await register(form);
      toast.success("Welcome to ComicVerse!");
      navigate(form.role === "teacher" ? "/teacher" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen halftone-yellow">
      <Navbar />
      <div className="max-w-md mx-auto p-6 mt-8">
        <div className="cv-border cv-shadow-lg rounded-2xl bg-white p-8">
          <h1
            className="font-headings uppercase text-4xl mb-2"
            data-testid="signup-title"
          >
            Create account
          </h1>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              type="button"
              onClick={() => setForm({ ...form, role: "parent" })}
              className={`cv-border-3 rounded-xl py-2 font-headings uppercase cv-press ${
                form.role === "parent"
                  ? "bg-yellow-300 cv-shadow-sm"
                  : "bg-white"
              }`}
              data-testid="role-parent"
            >
              Parent
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, role: "teacher" })}
              className={`cv-border-3 rounded-xl py-2 font-headings uppercase cv-press ${
                form.role === "teacher"
                  ? "bg-purple-400 cv-shadow-sm"
                  : "bg-white"
              }`}
              data-testid="role-teacher"
            >
              Teacher
            </button>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="font-headings uppercase">Your name</Label>
              <Input
                className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                required
                value={form.name}
                onChange={upd("name")}
                data-testid="signup-name"
              />
            </div>
            <div>
              <Label className="font-headings uppercase">Email</Label>
              <Input
                className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                type="email"
                required
                value={form.email}
                onChange={upd("email")}
                data-testid="signup-email"
              />
            </div>
            <div>
              <Label className="font-headings uppercase">Password</Label>
              <Input
                className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={upd("password")}
                data-testid="signup-password"
              />
            </div>
            <div>
              <Label className="font-headings uppercase">
                {form.role === "teacher" ? "Admin" : "Parent"} PIN (4+ digits)
              </Label>
              <Input
                className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                type="password"
                required
                pattern="[0-9]+"
                minLength={4}
                value={form.parent_pin}
                onChange={upd("parent_pin")}
                data-testid="signup-pin"
              />
              <p className="text-xs font-body mt-1 text-black/60">
                Used to lock subscription/billing settings.
              </p>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow cv-press font-headings text-xl uppercase rounded-xl py-6"
              data-testid="signup-submit"
            >
              {busy ? "Creating..." : "Create account"}
            </Button>
          </form>
          <p className="mt-6 font-body font-bold text-sm text-center">
            Have an account?{" "}
            <Link
              to="/login"
              className="text-purple-600 underline"
              data-testid="signup-to-login"
            >
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
